import React from 'react';
import {ScrollView, StyleSheet, Text, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {usePremiumLegacyTheme} from '../helpers/modeScreenTheme';
import {useManagementTheme} from '../helpers/managementTheme';
import {useCurrentUser} from '../helpers/useCurrentUser';
import {useMail} from '../helpers/MailContext';
import {useTopInset} from '../helpers/safeArea';
import {useServiceMode} from '../helpers/ServiceModeContext';
import useAiAssistantGate from './assistant/components/useAiAssistantGate';
import {useChatHubBadge} from '../helpers/teamChat/useChatHubBadge';
import {ListRow} from '../components/management/ManagementUI';
import {ActionTile, SectionHeader} from '../components/premium';
import ModeProfileCard from '../components/ModeProfileCard';

/**
 * Service ("Egyéb") menu: profile card, the management group as shortcut tiles
 * and the reports / system groups as tinted list rows. Same entries, gating and
 * navigation targets as before.
 *
 * @returns {JSX.Element} The menu screen.
 */
const RepairmanMoreScreen = () => {
    const { t } = useTranslation();
    const { theme } = usePremiumLegacyTheme();
    const m = useManagementTheme();
    const navigation = useNavigation();
    const { unreadCount } = useMail();
    const topInset = useTopInset();
    const { isFeatureEnabled } = useServiceMode();
    const { user: currentUser, fullName } = useCurrentUser();
    // "Ask anything" AI assistant — build-flag + store-level gate (function.aiAssistant).
    const aiAssistantEnabled = useAiAssistantGate();
    // Team Chat + meetings (unread count in the subtitle); hidden when chat is excluded.
    // Messenger hub entry: team chat + live support unread combined.
    const {visible: teamChatVisible, total: teamChatUnread} = useChatHubBadge();

    const handleNavigate = (screen) => {
        if (screen) {
            navigation.navigate(screen);
        }
    };

    const menuSections = [
        {
            title: t('repair_more_management'),
            tiles: true,
            items: [
                { icon: 'account-group-outline', label: t('repair_more_customers'), subtitle: t('repair_more_customers_sub'), tone: 'info', screen: 'CustomerVehicleScreen' },
                { icon: 'car-multiple', label: t('repair_more_vehicles'), subtitle: t('repair_more_vehicles_sub'), tone: 'success', screen: 'CustomerVehicleScreen', featureKey: 'vehicles' },
                { icon: 'package-variant-closed', label: t('repair_more_inventory'), subtitle: t('repair_more_inventory_sub'), tone: 'warning', screen: 'PartsInventoryScreen', featureKey: 'partsWarehouse' },
                ...(teamChatVisible ? [{ icon: 'forum-outline', label: t('team_chat_hub_title'), subtitle: teamChatUnread > 0 ? t('team_chat_unread_count', {count: teamChatUnread}) : t('team_chat_menu_subtitle'), tone: 'primary', screen: 'ChatHomeScreen' }] : []),
                { icon: 'email-outline', label: t('mail_title'), subtitle: unreadCount > 0 ? t('mail_unread_count', {count: unreadCount}) : t('mail_menu_subtitle'), tone: 'info', screen: 'MailHomeScreen' },
                // "Ask anything" AI assistant — cloned from the Mail entry above,
                // dropped when the store/build disables the module.
                ...(aiAssistantEnabled ? [{ icon: 'robot-outline', label: t('assistant_title', 'AI asszisztens'), subtitle: t('assistant_menu_subtitle', 'Kérdezz bármit — adatok, riportok és műveletek'), tone: 'primary', screen: 'AIAssistantScreen' }] : []),
            ].filter((item) => !item.featureKey || isFeatureEnabled(item.featureKey))
        },
        {
            title: t('repair_more_reports'),
            items: [
                { icon: 'chart-bar', label: t('repair_more_statistics'), subtitle: t('repair_more_statistics_sub'), tone: 'violet', screen: 'StatisticsScreen' },
                { icon: 'file-chart-outline', label: t('repair_more_invoices'), subtitle: t('repair_more_invoices_sub'), tone: 'danger', screen: 'WorkSheetListScreen' },
            ]
        },
        {
            title: t('repair_more_system'),
            items: [
                { icon: 'cog-outline', label: t('settings'), subtitle: t('repair_more_settings_sub'), tone: 'neutral', screen: 'SettingsScreen' },
                { icon: 'information-outline', label: t('repair_more_about'), subtitle: t('repair_more_about_sub'), tone: 'success' },
            ]
        }
    ];

    return (
        <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
            <ScrollView
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
            >
                {/* Header */}
                <View style={[styles.header, {paddingTop: topInset + 12}]}>
                    <Text style={[styles.headerTitle, { color: m.colors.heading }]}>{t('repair_more_title')}</Text>
                </View>

                <ModeProfileCard
                    user={currentUser}
                    name={fullName}
                    roleLabel={t('repair_technician')}
                    email={currentUser?.email ? String(currentUser.email) : undefined}
                    onPress={() => handleNavigate('SettingsScreen')}
                />

                {/* Menu Sections */}
                {menuSections.map((section, index) => (
                    <View key={index}>
                        <SectionHeader title={section.title} />
                        {section.tiles ? (
                            <View style={styles.tileGrid}>
                                {section.items.map((item, i) => (
                                    <ActionTile
                                        key={i}
                                        icon={item.icon}
                                        tone={item.tone}
                                        title={item.label}
                                        subtitle={item.subtitle}
                                        onPress={() => handleNavigate(item.screen)}
                                    />
                                ))}
                            </View>
                        ) : section.items.map((item, i) => (
                            <ListRow
                                key={i}
                                icon={item.icon}
                                iconTone={item.tone}
                                title={item.label}
                                subtitle={item.subtitle}
                                onPress={item.screen ? () => handleNavigate(item.screen) : undefined}
                            />
                        ))}
                    </View>
                ))}

                <View style={{ height: 120 }} />
            </ScrollView>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    scrollContent: {
        paddingHorizontal: 16,
    },
    header: {
        // paddingTop applied dynamically via useTopInset()
        paddingBottom: 16,
    },
    headerTitle: {
        fontSize: 30,
        fontWeight: '800',
        letterSpacing: -0.8,
    },
    tileGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
    },
});

export default RepairmanMoreScreen;
